import { useState } from "react"
import { Stack, Button, Spinner, Alert } from "react-bootstrap"
import CStack from "../../components/CStack"
import BaseControl from "../../components/BaseControl"
import Label from "../../components/Label"
import CAccordion from "../../components/CAccordion"
import axios from "axios"
import { ENDPOINT_COMPANY_SETTINGS_TEST_AUTH } from "../../constants"

interface DiagnosticsSectionProps {
    hostUrl?: string
    healthCheckPath?: string
}

interface AuthStep {
    name: string
    order: number
    success: boolean
    duration_ms: number
    details?: any
    error?: string
    suggestion?: string
}

interface AuthTestResult {
    success: boolean
    total_duration_ms: number
    steps: AuthStep[]
    suggestions?: string[]
    company_id?: string
    start_time?: string
    end_time?: string
}

const DiagnosticsSection = ({ hostUrl, healthCheckPath }: DiagnosticsSectionProps) => {
    // Authentication testing states
    const [testingAuth, setTestingAuth] = useState(false)
    const [authResult, setAuthResult] = useState<AuthTestResult | null>(null)
    const [currentStep, setCurrentStep] = useState(0)
    
    const testAuthentication = async () => {
        if (!hostUrl) return
        
        setTestingAuth(true)
        setAuthResult(null)
        setCurrentStep(0)
        
        try {
            // Run authentication test which includes 6 detailed steps:
            // 1. Network Connectivity to Mobile Banking Server
            // 2. CPASS Server Connectivity
            // 3. Request Challenge from Mobile Banking Server
            // 4. Sign Challenge with CPASS
            // 5. Verify CPASS Token with Mobile Banking Server
            // 6. Validate Access Token
            setCurrentStep(1)
            const response = await axios.post(ENDPOINT_COMPANY_SETTINGS_TEST_AUTH, {
                company_id: "", // Will use default from token
                client_id: ""    // Will use default from token
            })
            
            const data = response.data.Msg as AuthTestResult
            
            // Update current step based on response
            if (data.steps && data.steps.length > 0) {
                setCurrentStep(data.steps.length)
            }
            
            setAuthResult(data)
            
        } catch (error: any) {
            let errorMessage = "Authentication test failed"
            let suggestions: string[] = []
            
            if (error.response) {
                errorMessage = error.response.data?.msg || `Server error (${error.response.status})`
                suggestions.push("Check your authentication token")
                suggestions.push("Verify server is running")
            } else if (error.request) {
                errorMessage = "Network error - Could not reach backend"
                suggestions.push("Check your network connection")
                suggestions.push("Verify the backend server is accessible")
            } else {
                errorMessage = "Request configuration error"
                suggestions.push("Check the application configuration")
            }
            
            setAuthResult({
                success: false,
                total_duration_ms: 0,
                steps: [],
                suggestions
            })
        } finally {
            setTestingAuth(false)
            setCurrentStep(0)
        }
    }
    
    const isTestEnabled = Boolean(hostUrl && hostUrl.trim())
    
    // Helper functions for formatting
    const formatDuration = (ms: number) => {
        if (ms < 1000) {
            return `${ms}ms`
        }
        return `${(ms / 1000).toFixed(2)}s`
    }
    
    const getStepIcon = (success: boolean) => {
        return success ? "✅" : "❌"
    }
    
    return (
        <CStack padding="0">
            <BaseControl text="Host URL" caption={hostUrl || "Not configured"}>
                <Label fontWeight="400" textAlign="end" padding="">
                    {hostUrl || "-"}
                </Label>
            </BaseControl>
            
            <BaseControl text="Health Check Path" caption={healthCheckPath || "Not configured"}>
                <Label fontWeight="400" textAlign="end" padding="">
                    {healthCheckPath || "-"}
                </Label>
            </BaseControl>
            
            <BaseControl text="Connection" caption="Full CPASS authentication diagnostics">
                <Button 
                    size="sm" 
                    variant="primary"
                    disabled={!isTestEnabled || testingAuth}
                    onClick={testAuthentication}
                >
                    {testingAuth ? (
                        <Stack direction="horizontal" gap={1}>
                            <Spinner size="sm" />
                            {currentStep > 0 ? `Testing... Step ${currentStep} of 6` : "Initializing..."}
                        </Stack>
                    ) : (
                        "Test Connection"
                    )}
                </Button>
            </BaseControl>
            
            {/* Authentication Test Results */}
            {authResult && (
                <div style={{ marginTop: '1rem' }}>
                    {/* Overall Result */}
                    <Alert 
                        variant={authResult.success ? "success" : "danger"}
                        dismissible
                        onClose={() => setAuthResult(null)}
                    >
                        <Alert.Heading>
                            {authResult.success ? "All Tests Passed" : "Tests Failed"}
                        </Alert.Heading>
                        {authResult.total_duration_ms > 0 && (
                            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                Total Duration: {formatDuration(authResult.total_duration_ms)}
                            </div>
                        )}
                    </Alert>
                    
                    {/* Steps Display */}
                    {authResult.steps && authResult.steps.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                            <Label fontWeight="600" padding="0 0 0.5rem 0">
                                {`Test Results (${authResult.steps.length} steps):`}
                            </Label>
                            {authResult.steps.map((step, index) => (
                                <div key={index} style={{ marginBottom: '0.5rem' }}>
                                    <CAccordion 
                                        size="5"
                                        label={`${getStepIcon(step.success)} Step ${step.order}/${authResult.steps.length}: ${step.name} (${formatDuration(step.duration_ms)})`}
                                    >
                                        <div style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                                            {step.error && (
                                                <div style={{ marginBottom: '0.5rem', color: '#dc3545' }}>
                                                    <strong>Error:</strong> {step.error}
                                                </div>
                                            )}
                                            {step.suggestion && (
                                                <div style={{ marginBottom: '0.5rem', color: '#ffc107' }}>
                                                    <strong>Suggestion:</strong> {step.suggestion}
                                                </div>
                                            )}
                                            {step.details && (
                                                <div>
                                                    <strong>Details:</strong>
                                                    <div style={{ marginTop: '0.25rem' }}>
                                                        {Object.entries(step.details).map(([key, value]) => (
                                                            <div key={key}>
                                                                {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {!step.error && !step.suggestion && !step.details && (
                                                <div style={{ color: '#28a745' }}>
                                                    Test passed successfully
                                                </div>
                                            )}
                                        </div>
                                    </CAccordion>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Overall Suggestions */}
                    {authResult.suggestions && authResult.suggestions.length > 0 && (
                        <Alert variant="warning" style={{ marginTop: '1rem' }}>
                            <Alert.Heading>Troubleshooting Suggestions</Alert.Heading>
                            <ul style={{ marginBottom: 0, paddingLeft: '1.5rem' }}>
                                {authResult.suggestions.map((suggestion, idx) => (
                                    <li key={idx}>{suggestion}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                </div>
            )}
        </CStack>
    )
}

export default DiagnosticsSection